import React, { useState, useEffect } from 'react';
import { Link, useParams, useNavigate } from 'react-router-dom';
import axios from 'axios';
import ReactMarkdown from 'react-markdown';
import Header from './Header';
import Footer from './Footer';
import CommentSection from './CommentSection';
import NewsletterSubscription from './NewsletterSubscription';

const PostView = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [post, setPost] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [relatedPosts, setRelatedPosts] = useState([]);
  const [categories, setCategories] = useState([]);
  
  useEffect(() => {
    const fetchData = async () => {
      try {
        setLoading(true);
        
        // Fetch the specific post
        const postResponse = await axios.get(`/api/posts/${id}`);
        setPost(postResponse.data);
        
        // Fetch related posts (same category, excluding current post).
        // Exclude by the resolved post's _id — the route param may be a slug.
        const allPostsResponse = await axios.get('/api/posts');
        const related = allPostsResponse.data
          .filter(p => p._id !== postResponse.data._id && p.category === postResponse.data.category)
          .slice(0, 3);
        setRelatedPosts(related);
        
        // Fetch categories with post counts
        const categoriesResponse = await axios.get('/api/categories/with-counts');
        setCategories(categoriesResponse.data);
        
        setLoading(false);
      } catch (err) {
        console.error('Error fetching post:', err);
        setError('Failed to load post. Please try again later.');
        setLoading(false);
      }
    };

    if (id) {
      fetchData();
    }
  }, [id]);
  
  if (loading) {
    return (
      <>
        <Header />
        <div className="container mx-auto py-12">
          <div className="flex justify-center items-center h-64">
            <div className="spinner"></div>
          </div>
        </div>
        <Footer />
      </>
    );
  }
  
  if (error) {
    return (
      <>
        <Header />
        <div className="container mx-auto py-12">
          <div className="bg-red-50 border-l-4 border-red-500 p-4 mb-4">
            <p className="text-red-700">{error}</p>
          </div>
          <div className="text-center mt-8">
            <button 
              onClick={() => window.location.reload()}
              className="btn btn-primary mr-4"
            >
              Try Again
            </button>
            <Link to="/" className="btn btn-outline">
              Back to Home
            </Link>
          </div>
        </div>
        <Footer />
      </>
    );
  }
  
  if (!post) {
    return (
      <>
        <Header />
        <div className="container mx-auto py-12">
          <div className="text-center">
            <h2 className="text-2xl font-bold mb-4">Post Not Found</h2>
            <p className="mb-6">The post you're looking for doesn't exist or has been removed.</p>
            <Link to="/" className="btn btn-primary">
              Back to Home
            </Link>
          </div>
        </div>
        <Footer />
      </>
    );
  }
  
  return (
    <>
      <Header />
      
      <main className="py-12 bg-gray-100">
        <div className="container mx-auto px-4">
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
            {/* Main Content */}
            <div className="lg:col-span-2">
              <article className="single-post">
                <header className="single-post-header">
                  <div className="category-badge">{post.category}</div>
                  <h1 className="single-post-title">{post.title}</h1>
                  
                  <div className="single-post-meta">
                    <div className="w-10 h-10 rounded-full bg-primary-100 flex items-center justify-center text-primary-700 font-bold">
                      {post.author.replace(/^Dr\.?\s+/i, "").charAt(0).toUpperCase()}
                    </div>
                    <div>
                      <p className="font-medium text-secondary-800">{post.author}</p>
                      <p className="text-sm text-secondary-500">
                        {new Date(post.createdAt).toLocaleDateString('en-US', { 
                          month: 'long', 
                          day: 'numeric', 
                          year: 'numeric' 
                        })}
                      </p>
                    </div>
                  </div>
                </header>
                
                {post.imageUrl && (
                  <img 
                    src={post.imageUrl} 
                    alt={post.title} 
                    className="single-post-img" 
                  />
                )}
                
                <div className="single-post-content">
                  <ReactMarkdown>{post.content}</ReactMarkdown>
                </div>
                
                {post.tags && post.tags.length > 0 && (
                  <div className="tags">
                    {post.tags.map((tag, index) => (
                      <Link key={index} to={`/tags/${tag}`} className="tag">
                        #{tag}
                      </Link>
                    ))}
                  </div>
                )}
              </article>
              
              {/* Comments Section */}
              <CommentSection postId={post._id} />
            </div>
            
            {/* Sidebar */}
            <div className="lg:col-span-1">
              {/* Author Bio */}
              <div className="sidebar mb-6">
                <h3 className="sidebar-title">About the Author</h3>
                <div className="flex items-center mb-4">
                  <div className="w-16 h-16 rounded-full bg-primary-100 flex items-center justify-center text-primary-700 text-xl font-bold mr-4">
                    {post.author.replace(/^Dr\.?\s+/i, "").charAt(0).toUpperCase()}
                  </div>
                  <div>
                    <h4 className="font-bold text-lg">{post.author}</h4>
                    <p className="text-gray-600">AI Research Scientist</p>
                  </div>
                </div>
                <p className="text-gray-700 mb-4">
                  Expert in artificial intelligence with extensive experience in applying AI solutions to business problems.
                </p>
                <div className="flex space-x-3">
                  <a href="#" className="text-primary hover:text-primary-dark">
                    <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 24 24" aria-hidden="true">
                      <path d="M8.29 20.251c7.547 0 11.675-6.253 11.675-11.675 0-.178 0-.355-.012-.53A8.348 8.348 0 0022 5.92a8.19 8.19 0 01-2.357.646 4.118 4.118 0 001.804-2.27 8.224 8.224 0 01-2.605.996 4.107 4.107 0 00-6.993 3.743 11.65 11.65 0 01-8.457-4.287 4.106 4.106 0 001.27 5.477A4.072 4.072 0 012.8 9.713v.052a4.105 4.105 0 003.292 4.022 4.095 4.095 0 01-1.853.07 4.108 4.108 0 003.834 2.85A8.233 8.233 0 012 18.407a11.616 11.616 0 006.29 1.84" />
                    </svg>
                  </a>
                  <a href="#" className="text-primary hover:text-primary-dark">
                    <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 24 24" aria-hidden="true">
                      <path d="M19 0h-14c-2.761 0-5 2.239-5 5v14c0 2.761 2.239 5 5 5h14c2.762 0 5-2.239 5-5v-14c0-2.761-2.238-5-5-5zm-11 19h-3v-11h3v11zm-1.5-12.268c-.966 0-1.75-.79-1.75-1.764s.784-1.764 1.75-1.764 1.75.79 1.75 1.764-.783 1.764-1.75 1.764zm13.5 12.268h-3v-5.604c0-3.368-4-3.113-4 0v5.604h-3v-11h3v1.765c1.396-2.586 7-2.777 7 2.476v6.759z" />
                    </svg>
                  </a>
                </div>
              </div>
              
              {/* Related Posts */}
              <div className="sidebar">
                <h3 className="sidebar-title">Related Posts</h3>
                {relatedPosts.length > 0 ? (
                  <div className="space-y-4">
                    {relatedPosts.map(post => (
                      <div key={post._id} className="flex items-start">
                        {post.imageUrl && (
                          <img 
                            src={post.imageUrl} 
                            alt={post.title} 
                            className="w-20 h-20 object-cover rounded mr-4" 
                          />
                        )}
                        <div>
                          <h4 className="font-medium mb-1">
                            <Link to={`/post/${post.slug || post._id}`} className="hover:text-primary">
                              {post.title}
                            </Link>
                          </h4>
                          <p className="text-sm text-gray-500">
                            {new Date(post.createdAt).toLocaleDateString('en-US', { 
                              month: 'short', 
                              day: 'numeric', 
                              year: 'numeric' 
                            })}
                          </p>
                        </div>
                      </div>
                    ))}
                  </div>
                ) : (
                  <p className="text-gray-500">No related posts found.</p>
                )}
              </div>
              
              {/* Categories */}
              <div className="sidebar">
                <h3 className="sidebar-title">Categories</h3>
                <ul className="sidebar-categories">
                  {categories.map(category => (
                    <li key={category._id}>
                      <Link to={`/categories/${category.name}`}>
                        {category.name}
                        <span className="count">{category.count}</span>
                      </Link>
                    </li>
                  ))}
                </ul>
              </div>
              
              {/* Tags Cloud */}
              <div className="sidebar">
                <h3 className="sidebar-title">Popular Tags</h3>
                <div className="tags">
                  {post.tags && post.tags.map((tag, index) => (
                    <Link key={index} to={`/tags/${tag}`} className="tag">
                      #{tag}
                    </Link>
                  ))}
                  <Link to="/tags/artificial-intelligence" className="tag">
                    #artificial-intelligence
                  </Link>
                  <Link to="/tags/machine-learning" className="tag">
                    #machine-learning
                  </Link>
                  <Link to="/tags/deep-learning" className="tag">
                    #deep-learning
                  </Link>
                </div>
              </div>
            </div>
          </div>
        </div>
      </main>
      
      <NewsletterSubscription />
      
      <Footer />
    </>
  );
};

export default PostView;
